import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories } from '@/mocks/categories';
import { games } from '@/mocks/games';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'all';
  const [isAnimating, setIsAnimating] = useState(false);

  const filteredGames = activeCategory === 'all'
    ? games
    : games.filter(g => g.category === activeCategory);

  const handleCategorySwitch = (categoryId: string) => {
    if (categoryId === activeCategory) return;
    setIsAnimating(true);
    const newParams = new URLSearchParams(searchParams);
    if (categoryId === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', categoryId);
    }
    newParams.delete('game');
    setSearchParams(newParams);
    setTimeout(() => setIsAnimating(false), 300);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams]);

  return (
    <>
      <SEOHead
        title="麻将游戏分类 - 经典麻将,四川麻将,广东麻将等多种玩法 | 麻将游戏在线玩"
        description="探索多种麻将游戏分类：经典麻将、四川麻将血战到底、广东麻将推倒胡、上海麻将、台湾麻将、日本立直麻将等。找到最适合你的麻将玩法，免费在线畅玩！"
        keywords="麻将游戏分类,经典麻将,四川麻将,广东麻将,麻将玩法大全,在线麻将分类"
        canonicalUrl="https://mahjong-online.com/categories"
        ogTitle="麻将游戏分类 - 多种玩法任你选择 | 麻将游戏在线玩"
        ogDescription="探索多种麻将游戏分类，找到最适合你的麻将玩法，免费在线畅玩！"
        ogUrl="https://mahjong-online.com/categories"
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: '麻将游戏分类',
          description: '浏览所有麻将游戏分类，包括经典麻将、地方麻将、国际麻将和单机麻将',
          url: 'https://mahjong-online.com/categories',
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '游戏分类' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950">
                麻将游戏分类
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                多种麻将玩法随心选，找到最适合你的那一款
              </p>
            </div>

            <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySwitch(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
              {filteredGames.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无游戏</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-product-shop>
                  {filteredGames.map((game) => (
                    <Link
                      key={game.id}
                      to={`/categories?category=${activeCategory}&game=${game.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group bg-background-100 rounded-lg overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
                    >
                      <div className="relative h-48 md:h-56 overflow-hidden">
                        <img
                          src={game.image}
                          alt={game.name}
                          title={game.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 right-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                          {game.rating} 分
                        </div>
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                            {game.name}
                          </h3>
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{game.players}</span>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                          {game.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                            {game.difficulty}
                          </span>
                          <span className="text-xs text-foreground-500 flex items-center gap-1">
                            <i className="ri-user-line"></i> {game.playCount} 游玩
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}